import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { AuthProvider } from '@/app/providers/AuthProvider'
import { useAuthStore } from '@/features/auth'
import { EnvironmentProvider } from '@/features/environments/contexts/EnvironmentContext'
import { TenantProvider } from '@/features/tenants'
import { queryClient } from '@/shared/lib/queryClient'
import { routeTree } from './routeTree.gen'
import '@/app/globals.css'

// Set up a Router instance
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    auth: undefined!, // We'll set this before rendering
  },
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Router Provider with Auth Context
function App() {
  const auth = useAuthStore()

  return (
    <RouterProvider
      router={router}
      context={{
        auth: {
          isAuthenticated: auth.isAuthenticated, // Use validated auth state from Zustand
          isLoading: auth.isLoading,
          user: auth.user,
          login: auth.login,
          logout: auth.logout,
        },
      }}
    />
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TenantProvider>
          <EnvironmentProvider>
            <App />
            <ReactQueryDevtools initialIsOpen={false} />
          </EnvironmentProvider>
        </TenantProvider>
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>
)
